import {
	DetailField,
	DetailFieldsGrid,
	DetailSection
} from '@/components/ui/details-panel';
import { EntityDetailsHeader } from '@/components/ui/entity-details-header';
import { AuditTab } from '@/components/ui/audit-tab';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { VehicleDisplayItem, VehicleDisplayList } from '@/components/ui/vehicle-display-item';
import { ParkingSessionsListPanel } from '@/pages/parking-sessions/parking-sessions-list-panel';
import { Car, FileText, History } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ContractListItem } from './types';
import { ContractStatusBadge } from './status-badge';
import { formatCurrency, formatDateRange } from './utils';

interface ContractDetailsProps {
	contract: ContractListItem;
	showHeader?: boolean;
	onEdit?: (id: string) => void;
	onClone?: (id: string) => void;
	onDelete?: (id: string) => void;
	onOpenInNewWindow?: (id: string) => void;
	onClose?: () => void;
	onBack?: () => void;
	showBackButton?: boolean;
}

const getCustomerDisplayName = (contract: ContractListItem) => {
	const customer = contract.customer;

	if (customer.isCompany) {
		return customer.firmName ?? `#${contract.contractId.slice(-6)}`;
	}

	const fullName = [customer.firstName, customer.lastName]
		.filter(Boolean)
		.join(' ');

	return fullName || customer.firmName || `#${contract.contractId.slice(-6)}`;
};

export const ContractDetails = ({
	contract,
	showHeader,
	onEdit,
	onClone,
	onDelete,
	onOpenInNewWindow,
	onClose,
	onBack,
	showBackButton
}: ContractDetailsProps) => {
	const { t } = useTranslation('common');
	const { t: tC } = useTranslation('contracts');
	const [activeTab, setActiveTab] = useState('details');
	const { customer } = contract;
	const effectiveVehicleLimit =
		contract.simultaneousVehicleLimit ?? customer.categoryLimit ?? null;
	const displayName = getCustomerDisplayName(contract);
	const entityTypeName = contract.contractNumber
		? `#${contract.contractNumber}`
		: contract.isActive
			? tC('details.activeContract')
			: tC('details.inactiveContract');

	return (
		<div className="flex flex-col h-full min-h-0">
			{showHeader && (
				<EntityDetailsHeader
					icon={
						<div className="flex items-center justify-center w-14 h-14 rounded-full bg-transparent border-2 border-gray-400 dark:border-gray-500 flex-shrink-0">
							<FileText className="w-7 h-7 text-gray-600 dark:text-gray-300" />
						</div>
					}
					name={displayName}
					statusBadge={
						contract.status === 'active'
							? undefined
							: <ContractStatusBadge status={contract.status} />
					}
					entityTypeName={entityTypeName}
					entityId={contract.contractId}
					onEdit={onEdit}
					onClone={onClone}
					onDelete={onDelete}
					onOpenInNewWindow={onOpenInNewWindow}
					onClose={onClose}
					onBack={onBack}
					showBackButton={showBackButton}
					showCloseButton={!!onClose}
					className="pb-4 mb-4 border-b border-gray-200 dark:border-slate-700"
				/>
			)}

			<Tabs
				value={activeTab}
				onValueChange={setActiveTab}
				className="flex flex-col h-full min-h-0"
			>
				{/* Tab Bar - fixed height at top */}
				<div className="flex-shrink-0 pb-2">
					<TabsList className="gap-2 bg-transparent p-0">
						<TabsTrigger value="details" className="flex items-center gap-2 px-4 py-2 font-normal data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold">
							<FileText className="w-4 h-4" />
							<span>{t('tabs.details')}</span>
						</TabsTrigger>
						<TabsTrigger value="parkingSessions" className="flex items-center gap-2 px-4 py-2 font-normal data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold">
							<Car className="w-4 h-4" />
							<span>{t('tabs.parkingSessions')}</span>
						</TabsTrigger>
						<TabsTrigger value="audit" align="right" className="flex items-center gap-2 px-4 py-2 font-normal data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold">
							<History className="w-4 h-4" />
							<span>{t('tabs.audit')}</span>
						</TabsTrigger>
					</TabsList>
				</div>

				{/* Tab Content - scrollable area with min-height for stable modal sizing */}
				<div className="flex-1 min-h-[350px] overflow-y-auto overflow-x-hidden">
					<TabsContent value="details" className="space-y-6 mt-0">
						<DetailSection>
							<DetailFieldsGrid>
								<DetailField
									label={tC('details.validityPeriod')}
									value={formatDateRange(
										contract.beginDate,
										contract.endDate
									)}
								/>
								<DetailField
									label={tC('details.monthlyAmount')}
									value={formatCurrency(contract.amount ?? null)}
								/>
							</DetailFieldsGrid>
						</DetailSection>

						<DetailSection title={t('sections.renewal_access')}>
							<DetailFieldsGrid>
								<DetailField
									label={tC('details.autoRenewal')}
									value={
										contract.isAutoRenewed
											? tC('details.enabled')
											: tC('details.disabled')
									}
								/>
								{contract.preferredLanguage && (
									<DetailField
										label={tC('details.preferredLanguage')}
										value={
											contract.preferredLanguage === 'EN'
												? tC('form.preferredLanguageEN')
												: tC('form.preferredLanguageEL')
										}
									/>
								)}
								<DetailField
									label={tC('details.effectiveVehicleLimit')}
									value={
										effectiveVehicleLimit != null
											? `${effectiveVehicleLimit} ${tC('details.concurrentVehicles')}`
											: t('placeholders.inherit_from_customer')
									}
								/>
								<DetailField
									label={tC('details.reservedParkingSlots')}
									value={
										contract.reservedParkingSlots != null
											? `${contract.reservedParkingSlots} ${tC('details.slotsUnit')}`
											: tC('details.notDefined')
									}
								/>
								<DetailField
									label={tC('details.customerCategory')}
									value={customer.categoryName}
								/>
								<DetailField
									label={tC('details.categoryLimit')}
									value={
										customer.categoryLimit != null
											? `${customer.categoryLimit} ${tC('details.vehiclesUnit')}`
											: tC('details.notDefined')
									}
								/>
							</DetailFieldsGrid>
						</DetailSection>

						<DetailSection title={t('sections.assigned_vehicles')}>
							{contract.contractVehicles.length === 0 ? (
								<div className="text-sm text-gray-500">
									{tC('details.noVehicles')}
								</div>
							) : (
								<VehicleDisplayList>
									{contract.contractVehicles.map(vehicle => (
											<VehicleDisplayItem
												key={vehicle.contractVehicleId}
												vehicleId={vehicle.vehicleId}
												plateNumber={vehicle.plateNumber}
												vehicleTypeName={vehicle.vehicleTypeName}
												make={vehicle.make}
												model={vehicle.model}
												color={vehicle.color}
												tags={[
													vehicle.rfidTag
														? `${tC('details.rfidTag')}: ${vehicle.rfidTag}`
														: '',
													vehicle.isVip
														? tC('details.vipAccess')
														: tC('details.standardAccess')
												]}
											className="py-3 border-b border-border last:border-b-0 first:pt-0 last:pb-0"
										/>
									))}
								</VehicleDisplayList>
							)}
						</DetailSection>
					</TabsContent>

					<TabsContent value="parkingSessions" className="mt-0">
						<ParkingSessionsListPanel
							contractId={contract.contractId}
							isActive={activeTab === 'parkingSessions'}
						/>
					</TabsContent>

					<TabsContent value="audit" className="mt-0">
						<AuditTab
							data={
								{
									// Contract audit fields would come from the contract data
									// Adding placeholders for now since they're not in the current type
								}
							}
						/>
					</TabsContent>
				</div>
			</Tabs>
		</div>
	);
};
